if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.optionValueSelectionChangeUnsubscriber =
          typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
            ? subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onOptionValueSelectionChange.bind(this))
            : undefined;

        this.applyColorFilter('', true);
        window.setTimeout(() => this.applyColorFilter('', true));
      }

      disconnectedCallback() {
        this.optionValueSelectionChangeUnsubscriber?.();
      }

      onOptionValueSelectionChange(event) {
        const sourceEventTarget = event?.data?.event?.target;
        const productInfo = this.closest('product-info');

        if (sourceEventTarget && productInfo && !productInfo.contains(sourceEventTarget)) return;

        window.setTimeout(() => this.applyColorFilter('', true));
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.applyColorFilter(mediaId, prepend);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      getSelectedColor() {
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects?.getAllSelectedOptions) return '';

        const selectedColor = variantSelects.getAllSelectedOptions().find(({ name }) => {
          if (String(name || '').trim() === '颜色') return true;
          const normalizedName = this.normalizeColorValue(name);
          return normalizedName === 'color' || normalizedName === 'colour' || normalizedName === 'yan-se';
        });

        return this.normalizeColorValue(selectedColor?.value || '');
      }

      normalizeColorValue(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      rememberOriginalPositions(mediaItems) {
        mediaItems.forEach((item, index) => {
          if (!item.dataset.originalMediaPosition) item.dataset.originalMediaPosition = String(index);

          const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${item.dataset.mediaId}"]`);
          if (thumbnail && !thumbnail.dataset.originalMediaPosition) thumbnail.dataset.originalMediaPosition = String(index);
        });
      }

      getMediaAlt(item) {
        return item.querySelector('img')?.getAttribute('alt') || '';
      }

      getMediaSortValue(item, selectedColor) {
        const normalizedAlt = this.normalizeColorValue(this.getMediaAlt(item));
        const originalPosition = Number(item.dataset.originalMediaPosition || 0);
        const colorPrefixMatch = normalizedAlt.match(new RegExp(`(?:^|-)${selectedColor}-(\\d{1,3})(?:-|$)`));
        const anyNumberMatch = normalizedAlt.match(/(?:^|-)(\d{1,3})(?:-|$)/);
        const explicitOrder = colorPrefixMatch?.[1] || anyNumberMatch?.[1];

        if (item.dataset.mediaColor === selectedColor && explicitOrder) return Number(explicitOrder);
        if (item.dataset.mediaColor === selectedColor) return 1000 + originalPosition;
        return 2000 + originalPosition;
      }

      sortVisibleMedia(selectedColor, mediaItems) {
        const visibleItems = mediaItems.filter((item) => !item.hidden);
        const sortedItems = visibleItems.sort((a, b) => {
          const orderDifference = this.getMediaSortValue(a, selectedColor) - this.getMediaSortValue(b, selectedColor);
          if (orderDifference !== 0) return orderDifference;
          return Number(a.dataset.originalMediaPosition || 0) - Number(b.dataset.originalMediaPosition || 0);
        });

        sortedItems.forEach((item) => {
          this.elements.viewer.querySelector('ul, ol')?.append(item);
          const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${item.dataset.mediaId}"]`);
          if (thumbnail) this.elements.thumbnails.querySelector('ul, ol')?.append(thumbnail);
        });

        return sortedItems;
      }

      activateMedia(media) {
        if (!media) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => item.classList.remove('is-active'));
        media.classList.add('is-active');
        this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${media.dataset.mediaId}"]`));
      }

      applyColorFilter(preferredMediaId = '', useSortedFirst = false) {
        if (!this.elements.viewer) return;

        const selectedColor = this.getSelectedColor();
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        if (!selectedColor || !mediaItems.length) return;
        this.rememberOriginalPositions(mediaItems);

        const matchingItems = mediaItems.filter((item) => item.dataset.mediaColor === selectedColor);
        if (!matchingItems.length) {
          mediaItems.forEach((item) => (item.hidden = false));
          this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => (thumbnail.hidden = false));
          this.elements.viewer.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
          return;
        }

        mediaItems.forEach((item) => {
          item.hidden = Boolean(item.dataset.mediaColor && item.dataset.mediaColor !== selectedColor);
        });

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
          thumbnail.hidden = Boolean(thumbnail.dataset.mediaColor && thumbnail.dataset.mediaColor !== selectedColor);
        });
        const sortedItems = this.sortVisibleMedia(selectedColor, mediaItems);

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active:not([hidden])');
        if (useSortedFirst && sortedItems[0]) {
          this.activateMedia(sortedItems[0]);
        } else if (!activeMedia) {
          const preferredMedia = preferredMediaId
            ? this.elements.viewer.querySelector(`[data-media-id="${preferredMediaId}"]:not([hidden])`)
            : null;
          const nextMedia = preferredMedia || this.elements.viewer.querySelector('[data-media-id]:not([hidden])');

          this.activateMedia(nextMedia);
        }

        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
